import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { techniques, filterTechniques } from "../data/techniques.js";
import { Icon, familyMeta } from "../components/Icons.jsx";
import Seo from "../components/Seo.jsx";

const familyOpts = ["all", "breath", "sound", "body", "emotion", "space", "sleep", "love", "daily"];
const timeOpts = [
  { v: "all", label: "Any" },
  { v: "3", label: "≤ 3 min" },
  { v: "5", label: "≤ 5 min" },
  { v: "10", label: "≤ 10 min" },
];
const levelOpts = [
  { v: "all", label: "All levels" },
  { v: "1", label: "Beginner" },
  { v: "2", label: "Intermediate" },
  { v: "3", label: "Deep" },
];

export default function TechniquesIndex() {
  const [params, setParams] = useSearchParams();
  const family = params.get("family") || "all";
  const time = params.get("time") || "all";
  const level = params.get("level") || "all";
  const [query, setQuery] = useState(params.get("q") || "");

  const list = useMemo(
    () => filterTechniques({ family, time, level, query }),
    [family, time, level, query]
  );

  const set = (key, val) => {
    const next = new URLSearchParams(params);
    if (!val || val === "all") next.delete(key);
    else next.set(key, val);
    setParams(next, { replace: true });
  };

  return (
    <>
      <Seo
        title="All 112 Techniques · Simple Meditation World"
        description="Browse the complete map of 112 meditation doorways from the Vigyan Bhairava Tantra. Filter by family, time, and difficulty."
        path="/techniques"
      />
      <section className="section-night" style={{ paddingTop: 80, paddingBottom: 36 }}>
        <div className="container">
          <span className="eyebrow" style={{ color: "var(--gold)" }}>The map</span>
          <h1 style={{ color: "var(--ivory)", fontFamily: "var(--serif)", fontWeight: 400, fontSize: "clamp(36px, 5vw, 60px)", lineHeight: 1.05, marginTop: 14 }}>
            All 112 <em style={{ color: "var(--gold)", fontStyle: "italic" }}>doorways</em>
          </h1>
          <p style={{ color: "rgba(248,241,231,0.72)", maxWidth: 640, marginTop: 16 }}>
            Every technique from the Vigyan Bhairava Tantra, organized into eight families. Filter by what you have time for and where you are in your practice.
          </p>
        </div>
      </section>

      <section className="section-ivory" style={{ paddingTop: 36 }}>
        <div className="container">
          <div className="filter-bar" style={{ marginBottom: 28 }}>
            <div className="filter-group">
              <span className="filter-chip" style={{ color: "var(--copper)", cursor: "default" }}>Family</span>
              {familyOpts.map((f) => (
                <button key={f} className={`filter-chip ${family === f ? "active" : ""}`} onClick={() => set("family", f)}>
                  {f === "all" ? "All" : familyMeta[f].label}
                </button>
              ))}
            </div>
            <div className="filter-group">
              <span className="filter-chip" style={{ color: "var(--copper)", cursor: "default" }}>Time</span>
              {timeOpts.map((t) => (
                <button key={t.v} className={`filter-chip ${time === t.v ? "active" : ""}`} onClick={() => set("time", t.v)}>{t.label}</button>
              ))}
            </div>
            <div className="filter-group">
              <span className="filter-chip" style={{ color: "var(--copper)", cursor: "default" }}>Level</span>
              {levelOpts.map((l) => (
                <button key={l.v} className={`filter-chip ${level === l.v ? "active" : ""}`} onClick={() => set("level", l.v)}>{l.label}</button>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <input
                type="search"
                value={query}
                onChange={(e) => { setQuery(e.target.value); set("q", e.target.value); }}
                placeholder="Search by title, theme, or word..."
                aria-label="Search techniques"
                style={{ width: "100%", padding: "10px 14px", border: "1px solid var(--border)", borderRadius: 999, background: "#fff", fontFamily: "inherit", fontSize: 14 }}
              />
            </div>
          </div>

          <div style={{ fontFamily: "var(--mono)", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--muted)", marginBottom: 24 }}>
            Showing {list.length} of {techniques.length} techniques
          </div>

          <div className="techniques-grid">
            {list.map((t) => {
              const fam = familyMeta[t.family];
              return (
                <Link key={t.id} to={`/technique/${t.id}`} className="t-card">
                  <div className="t-card-top">
                    <span className="t-num">{String(t.id).padStart(3, "0")}</span>
                    <span className="t-fam">{fam.icon(16)} {fam.label}</span>
                  </div>
                  <h3>{t.title}</h3>
                  <p>{t.modern}</p>
                  <div className="t-card-meta">
                    <span><b>{t.time === 0 ? "—" : `${t.time} min`}</b></span>
                    <span className="dt"></span>
                    <span>Level {t.level}</span>
                    <span className="t-card-arrow">{Icon.arrowSm()}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
